import { useCallback, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { api } from "../../api";
import type {
  EntityType,
  JobStatus,
  MyPayEnvironment,
  TargetEnvironment,
  ValidationView,
} from "../../generated/core-api";
import { onboardingJobHookError } from "../utils/backendErrorMessage";
import { isTerminalJobStatus, useOnboardingJobPoll } from "./useOnboardingJobPoll";

export interface InvokeOnboardingValidationArgs {
  /** Workload the validation runs against — from `useOnboardingWorkload`. */
  workloadId: string;
  entityType: EntityType;
  /**
   * When true, run Christmas `validateOnly` after Pacific validation (requires
   * `ONBOARDING_MIGRATION_RUNS_ENABLED` — Api #1051 shipped this dark: false in
   * Sandbox/Preview/Production until a deliberate cutover decision).
   * Omitted/false = Pacific-only validation.
   */
  destinationDryRun?: boolean;
  /** Target Datapay environment for the destination dry-run. */
  mypayEnvironment?: MyPayEnvironment;
  /** Credential tier for the Christmas dry-run — Slice 5 accepts `"test"` only. */
  targetEnvironment?: TargetEnvironment;
}

export interface OnboardingValidationState {
  invoke: (args: InvokeOnboardingValidationArgs) => void;
  validationId: string | null;
  status: JobStatus | null;
  /** Full read DTO from the latest poll; null until the first successful poll response. */
  data: ValidationView | null;
  /** The start-validation call failing. A terminal `status: "failed"` is surfaced via `status`/`data.report`, not this field. */
  error: string | null;
  isInvoking: boolean;
  isPolling: boolean;
  reset: () => void;
}

/**
 * Run the shared validator on demand (`POST …/workloads/{workloadId}/entities/{entityType}/validations`)
 * and poll it to terminal on the workload-scoped route
 * (`GET …/workloads/{workloadId}/validations/{validationId}`) — the LADR-039 Track 2
 * replacement for the Track 1 `invokeAgent` `mode: "validate"` step (see
 * `.plans/ladr-039-contract-mapping.md`'s shared shape reference).
 *
 * The poll route is scoped only by `workloadId` + `validationId`, not `entityType` — a
 * validation id is unique within the workload partition — so `entityType` is only needed
 * on the invoke call.
 */
export function useOnboardingValidation(): OnboardingValidationState {
  const [workloadId, setWorkloadId] = useState<string | null>(null);
  const [validationId, setValidationId] = useState<string | null>(null);

  const invokeMutation = useMutation({
    mutationFn: (args: InvokeOnboardingValidationArgs) =>
      api.createOnboardingEntityValidation(args.workloadId, args.entityType, {
        destination_dry_run: args.destinationDryRun,
        mypay_environment: args.mypayEnvironment,
        target_environment: args.targetEnvironment,
      }),
    onSuccess: (data, variables) => {
      setWorkloadId(variables.workloadId);
      setValidationId(data.validation_id);
    },
  });

  const pollQuery = useOnboardingJobPoll<ValidationView>(
    ["onboardingValidation", workloadId, validationId],
    () => api.getOnboardingValidation(workloadId as string, validationId as string),
    !!workloadId && !!validationId
  );

  const status = pollQuery.data?.status ?? null;

  const invoke = useCallback((args: InvokeOnboardingValidationArgs) => {
    setValidationId(null);
    invokeMutation.mutate(args);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reset = useCallback(() => {
    setWorkloadId(null);
    setValidationId(null);
    invokeMutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    invoke,
    validationId,
    status,
    data: pollQuery.data ?? null,
    error: onboardingJobHookError(invokeMutation.error, pollQuery.error),
    isInvoking: invokeMutation.isPending,
    isPolling:
      !!validationId && !isTerminalJobStatus(status) && !pollQuery.isError,
    reset,
  };
}
